/** @jsxImportSource @emotion/react */
import { withMyTheme } from "../../../../theme/theme"
import { css } from "@mui/material"
import { Box, TextField, Avatar, IconButton } from "@mui/material"
import { useState, useRef } from "react"
import { useTranslation } from "react-i18next"
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate"
import { ImageCropDialog } from "./ImageCropDialog"

const ContainerStyle = withMyTheme((theme) => css`
    display: flex;
    align-items: center;
    gap: 1.5rem;
    width: 100%;
`)

const TextFieldStyle = withMyTheme((theme) => css`
    flex: 1;
    
    & .MuiInputLabel-root {
        font-family: ${theme.typography.body1.fontFamily};
        color: ${theme.palette.text.primary};
        font-size: 1rem;
        background: white;
        padding: 0 6px;
        
        &.Mui-focused {
            color: ${theme.palette.primary.main};
        }
    }
    
    & .MuiOutlinedInput-root {
        border-radius: 12px;
        background-color: white;
        
        &:hover .MuiOutlinedInput-notchedOutline {
            border-color: ${theme.palette.primary.main};
        }
        
        &.Mui-focused .MuiOutlinedInput-notchedOutline {
            border-color: ${theme.palette.primary.main};
            border-width: 2px;
        }
    }
    
    & .MuiInputBase-input {
        font-family: ${theme.typography.body1.fontFamily};
        font-size: 1rem;
        padding: 14px 16px;
    }
`)

const ImageButtonStyle = withMyTheme((theme) => css`
    width: 80px;
    height: 80px;
    border: 2px dashed ${theme.palette.primary.main};
    background-color: ${theme.palette.grey[50]};
    
    &:hover {
        background-color: ${theme.palette.grey[100]};
        border-color: ${theme.palette.primary.dark};
    }
`)

const AvatarStyle = withMyTheme((theme) => css`
    width: 80px;
    height: 80px;
    cursor: pointer;
    border: 2px solid ${theme.palette.primary.main};
    
    &:hover {
        opacity: 0.8;
    }
`)

interface WitnessInputProps {
    label: string
    name: string
    image: string
    onNameChange: (name: string) => void
    onImageChange: (image: string) => void
}

export const WitnessInput = ({
    label,
    name,
    image,
    onNameChange,
    onImageChange
}: WitnessInputProps) => {
    const { t } = useTranslation()
    const [cropDialogOpen, setCropDialogOpen] = useState(false)
    const [tempImage, setTempImage] = useState<string>('')
    const fileInputRef = useRef<HTMLInputElement>(null)

    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0]
        if (file) {
            const reader = new FileReader()
            reader.onload = () => {
                setTempImage(reader.result as string)
                setCropDialogOpen(true)
            }
            reader.readAsDataURL(file)
        }
        // Reset input so same file can be selected again
        if (fileInputRef.current) {
            fileInputRef.current.value = ''
        }
    }

    const handleImageClick = () => {
        fileInputRef.current?.click()
    }

    const handleCropComplete = (croppedImage: string) => {
        onImageChange(croppedImage)
        setCropDialogOpen(false)
        setTempImage('')
    }

    const handleCloseCropDialog = () => {
        setCropDialogOpen(false)
        setTempImage('')
    }

    return (
        <Box css={ContainerStyle}>
            <TextField
                label={label}
                value={name}
                onChange={(e) => onNameChange(e.target.value)}
                variant="outlined"
                fullWidth
                InputLabelProps={{
                    shrink: true,
                }}
                css={TextFieldStyle}
            />

            <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={handleFileSelect}
            />

            {image ? (
                <Avatar
                    src={image}
                    css={AvatarStyle}
                    onClick={handleImageClick}
                />
            ) : (
                <IconButton
                    css={ImageButtonStyle}
                    onClick={handleImageClick}
                >
                    <AddPhotoAlternateIcon sx={{ fontSize: 40 }} color="primary" />
                </IconButton>
            )}

            {tempImage && (
                <ImageCropDialog
                    open={cropDialogOpen}
                    image={tempImage}
                    onClose={handleCloseCropDialog}
                    onCropComplete={handleCropComplete}
                />
            )}
        </Box>
    )
}
